import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Pagination,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import { APP_COLORS as C } from "../../theme/colors";

export const STATUS_COLOR = {
  draft: "warning",
  submitted: "info",
  approved: "success",
  rejected: "error",
};

const STATUS_STRIP = {
  draft: "#ed6c02",
  submitted: "#0288d1",
  approved: "#2e7d32",
  rejected: "#d32f2f",
};

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
}

function Meta({ icon, children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: C.textMuted }}>
      {React.cloneElement(icon, { sx: { fontSize: 14 } })}
      <Typography variant="caption" noWrap>
        {children}
      </Typography>
    </Box>
  );
}

export default function SketchList({
  sketches,
  loading,
  activeId,
  userId,
  statusFilter,
  page,
  pages,
  total,
  pageSize,
  onFilter,
  onPage,
  onOpen,
  onNew,
  onDelete,
  onRefresh,
}) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* header */}
      <Box sx={{ px: 2, pt: 1.5, pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: C.primary, lineHeight: 1.2 }}>
            My sketches
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {total} sketch{total === 1 ? "" : "es"}
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={onRefresh}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button size="small" variant="contained" disableElevation startIcon={<AddIcon />} onClick={onNew}>
          New
        </Button>
      </Box>

      <Box sx={{ px: 2, pb: 1.25 }}>
        <Select
          size="small"
          fullWidth
          displayEmpty
          value={statusFilter}
          onChange={(e) => onFilter(e.target.value)}
          sx={{ fontSize: 13, bgcolor: "#fff" }}
        >
          <MenuItem value="">All statuses</MenuItem>
          <MenuItem value="draft">Draft</MenuItem>
          <MenuItem value="submitted">Submitted</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
        </Select>
      </Box>

      {/* cards */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1.5, pb: 1, position: "relative" }}>
        {loading && (
          <Box sx={{ position: "absolute", top: 8, right: 16 }}>
            <CircularProgress size={18} />
          </Box>
        )}

        {!loading && sketches.length === 0 && (
          <Box sx={{ textAlign: "center", color: C.textMuted, px: 2, py: 5 }}>
            <DrawOutlinedIcon sx={{ fontSize: 40, opacity: 0.5 }} />
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              {statusFilter ? `No ${statusFilter} sketches` : "No sketches yet"}
            </Typography>
            {!statusFilter && (
              <Typography variant="caption">
                Click “New” to start drawing new features.
              </Typography>
            )}
          </Box>
        )}

        {sketches.map((s) => {
          const active = s.sketch_id === activeId;
          const canDelete =
            s.created_by === userId && ["draft", "rejected"].includes(s.status);
          return (
            <Box
              key={s.sketch_id}
              onClick={() => onOpen(s.sketch_id)}
              sx={{
                position: "relative",
                mb: 1,
                p: 1.25,
                pl: 1.75,
                cursor: "pointer",
                borderRadius: 2,
                bgcolor: active ? C.primaryLight : C.surface,
                border: `1px solid ${active ? C.primaryMid : C.border}`,
                borderLeft: `4px solid ${STATUS_STRIP[s.status] || C.border}`,
                transition: "box-shadow .15s, border-color .15s",
                "&:hover": {
                  borderColor: C.primaryMid,
                  boxShadow: "0 2px 8px rgba(0,51,118,.12)",
                },
                "&:hover .del": { opacity: 1 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, flex: 1, color: C.text }} noWrap>
                  {s.name}
                </Typography>
                <Chip
                  size="small"
                  label={s.status}
                  color={STATUS_COLOR[s.status] || "default"}
                  sx={{ height: 20, fontSize: 11, textTransform: "capitalize" }}
                />
                {canDelete && (
                  <Tooltip title="Delete sketch">
                    <IconButton
                      className="del"
                      size="small"
                      color="error"
                      sx={{ opacity: 0.55, transition: "opacity .15s", p: 0.25 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(s);
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", columnGap: 1.5, rowGap: 0.25 }}>
                <Meta icon={<PlaceOutlinedIcon />}>
                  {s.feature_count} feature{s.feature_count === 1 ? "" : "s"}
                </Meta>
                <Meta icon={<ScheduleIcon />}>{formatDate(s.updated_at)}</Meta>
              </Box>
              {s.status === "rejected" && s.review_remarks && (
                <Box sx={{ mt: 0.75, px: 1, py: 0.5, borderRadius: 1, bgcolor: "#ffebee" }}>
                  <Typography variant="caption" sx={{ color: "#c62828" }}>
                    <b>Rejected:</b> {s.review_remarks}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* pagination */}
      {pages > 0 && (
        <Box
          sx={{
            borderTop: `1px solid ${C.border}`,
            px: 1.5,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#fafbfc",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {from}–{to} of {total}
          </Typography>
          <Pagination
            size="small"
            count={pages}
            page={page}
            siblingCount={0}
            boundaryCount={1}
            color="primary"
            onChange={(_, p) => onPage(p)}
          />
        </Box>
      )}
    </Box>
  );
}
